import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  ExpandMore,
  Schedule,
  PlayArrow,
  Pause,
  CheckCircle,
  Error,
  Delete,
  Refresh
} from '@mui/icons-material';
import { Config } from '../config/config';
import { ScheduledRecordingService, ScheduledClip } from '../service/scheduledRecording.service';

interface ScheduledDay {
  date: string;
  clips: ScheduledClip[];
  total_clips: number;
}

interface CameraSchedule {
  camera_id: string;
  camera_name: string;
  scheduled_days: ScheduledDay[];
  total_scheduled_days: number;
}

interface AccountCamera {
  id: string;
  name: string;
  [key: string]: any;
}

interface AccountWithSchedule {
  account_id: string;
  account_name: string;
  cameras: AccountCamera[];
  [key: string]: any;
}

interface ScheduledRecordingsListProps {
  accounts: AccountWithSchedule[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  totalCameras?: number;
  totalRecordings?: number;
}

const ScheduledRecordingsList: React.FC<ScheduledRecordingsListProps> = ({
  accounts,
  loading = false,
  error = null,
  onRefresh,
  totalCameras = 0,
  totalRecordings = 0
}: ScheduledRecordingsListProps) => {
  const [scheduledRecordings, setScheduledRecordings] = useState<Map<string, CameraSchedule>>(new Map());
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [activeRecordings, setActiveRecordings] = useState<any[]>([]);
  const [recordingStats, setRecordingStats] = useState<any>(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{camera_id: string, camera_name: string, date: string} | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  useEffect(() => {
    // Always load scheduled recordings, even if no accounts/cameras available
    // This ensures we show scheduled recordings even when recording service can't fetch cameras
    loadAllScheduledRecordings();
    loadActiveRecordings();
  }, []);

  useEffect(() => {
    // Auto-refresh active recordings every 10 seconds
    const interval = setInterval(() => {
      loadActiveRecordings();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const loadAllScheduledRecordings = async () => {
    setLoadingSchedules(true);
    const scheduleMap = new Map<string, CameraSchedule>();

    try {
      // Load all clips from the database using the service
      const data = await ScheduledRecordingService.getAllClips();
      const allClips = data.clips_sample || [];
      
      console.log(`📊 Loaded ${allClips.length} clips from database`);
      
      // Group clips by camera_id
      const clipsByCamera = new Map<string, ScheduledClip[]>();
      allClips.forEach((clip: ScheduledClip) => {
        if (!clipsByCamera.has(clip.camera_id)) {
          clipsByCamera.set(clip.camera_id, []);
        }
        clipsByCamera.get(clip.camera_id)!.push(clip);
      });
      
      // Convert to CameraSchedule format
      clipsByCamera.forEach((clips, cameraId) => {
        // Find camera name from accounts
        let cameraName = cameraId;
        for (const account of accounts) {
          const camera = account.cameras.find(c => c.id === cameraId);
          if (camera) {
            cameraName = camera.name;
            break;
          }
        }
        
        // Group clips by date
        const clipsByDate = new Map<string, ScheduledClip[]>();
        clips.forEach(clip => {
          const dateKey = clip.scheduled_date ? new Date(clip.scheduled_date).toISOString().split('T')[0] : 'unknown';
          if (!clipsByDate.has(dateKey)) {
            clipsByDate.set(dateKey, []);
          }
          clipsByDate.get(dateKey)!.push(clip);
        });
        
        const scheduledDays: ScheduledDay[] = Array.from(clipsByDate.entries()).map(([date, dayClips]) => ({
          date,
          clips: dayClips.sort((a, b) => (a.clip_index || 0) - (b.clip_index || 0)),
          total_clips: dayClips.length
        }));
        
        scheduleMap.set(cameraId, {
          camera_id: cameraId,
          camera_name: cameraName,
          scheduled_days: scheduledDays,
          total_scheduled_days: scheduledDays.length
        });
      });
      
      console.log(`📋 Processed clips for ${scheduleMap.size} cameras`);
    } catch (err) {
      console.error('Failed to load scheduled recordings:', err);
    } finally {
      setLoadingSchedules(false);
      setScheduledRecordings(scheduleMap);
    }
  };

  const loadActiveRecordings = async () => {
    setLoadingActive(true);
    
    try {
      console.log('🎬 Loading active recordings from recording service...');
      
      // Load active recordings from the recording service via backend proxy
      const recordingServiceUrl = `${Config.apiRoot}/recording-proxy`;
      const activeResponse = await fetch(`${recordingServiceUrl}/recordings`);
      if (activeResponse.ok) {
        const activeData = await activeResponse.json();
        const activeRecordings = (activeData || []).filter((rec: any) => rec.status === 'recording');
        setActiveRecordings(activeRecordings);
        console.log(`✅ Loaded ${activeRecordings.length} active recordings`);
      }

      // Recording statistics - for now just show count of active recordings
      setRecordingStats({
        active_count: activeRecordings.length,
        total_planned: getTotalScheduledClips(),
        cameras_with_schedules: getScheduledCamerasCount()
      });

    } catch (err) {
      console.error('❌ Failed to load active recordings:', err);
    } finally {
      setLoadingActive(false);
    }
  };

  const handleRefresh = () => {
    loadAllScheduledRecordings();
    loadActiveRecordings();
    onRefresh?.();
  };

  const handleToggleAccount = (accountId: string) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'default';
      case 'recording': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <Schedule fontSize="small" />;
      case 'recording': return <PlayArrow fontSize="small" />;
      case 'completed': return <CheckCircle fontSize="small" />;
      case 'failed': return <Error fontSize="small" />;
      case 'cancelled': return <Pause fontSize="small" />;
      default: return <Schedule fontSize="small" />;
    }
  };

  const formatDateTime = (dateTime: string, filename?: string) => {
    if (!dateTime) {
      return 'Invalid Date';
    }
    
    try {
      const baseDate = new Date(dateTime);
      if (isNaN(baseDate.getTime())) {
        return 'Invalid Date';
      }
      
      // Try to extract time from filename if available
      if (filename) {
        // Extract time from filename pattern: camera_id_YYYY-MM-DD_HHMM.mp4
        const timeMatch = filename.match(/_(\d{4})\.mp4$/);
        if (timeMatch) {
          const timeStr = timeMatch[1]; // HHMM format
          const hours = parseInt(timeStr.substring(0, 2), 10);
          const minutes = parseInt(timeStr.substring(2, 4), 10);
          
          // Set the time on the base date
          const fullDate = new Date(baseDate);
          fullDate.setHours(hours, minutes, 0, 0);
          
          return fullDate.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }
      
      // Fallback to original date
      return baseDate.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'for dateTime:', dateTime);
      return 'Invalid Date';
    }
  };

  const getTotalScheduledClips = () => {
    return Array.from(scheduledRecordings.values())
      .reduce((sum: number, schedule: CameraSchedule) => 
        sum + (schedule.scheduled_days || []).reduce((daySum: number, day: ScheduledDay) => 
          daySum + (day?.total_clips || 0), 0), 0);
  };

  const getScheduledCamerasCount = () => {
    return Array.from(scheduledRecordings.values())
      .filter((schedule: CameraSchedule) => (schedule?.total_scheduled_days || 0) > 0).length;
  };

  const handleDeleteDay = (camera_id: string, camera_name: string, date: string) => {
    // Konvertiere das Datum zu YYYY-MM-DD Format für die API
    const dateOnly = new Date(date).toISOString().split('T')[0];
    setDeleteTarget({ camera_id, camera_name, date: dateOnly });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDay = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const result = await ScheduledRecordingService.deleteScheduledDay(
        deleteTarget.camera_id, 
        deleteTarget.date
      );
      
      console.log(`✅ Deleted ${result.data.deleted_clips || result.data.cancelled_clips} clips for ${deleteTarget.camera_name} on ${deleteTarget.date}`);
      
      // Refresh data
      loadAllScheduledRecordings();
      
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      
    } catch (error: any) {
      console.error('Failed to delete scheduled day:', error);
      alert(`❌ Fehler beim Löschen: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteAll = () => {
    setDeleteAllDialogOpen(true);
  };

  const confirmDeleteAll = async () => {
    setDeletingAll(true);
    
    try {
      // Delete all scheduled recordings for all cameras
      for (const schedule of Array.from(scheduledRecordings.values())) {
        for (const day of schedule.scheduled_days) {
          try {
            const result = await ScheduledRecordingService.deleteScheduledDay(
              schedule.camera_id,
              day.date
            );
            console.log(`✅ Deleted ${result.data.deleted_clips || result.data.cancelled_clips} clips for ${schedule.camera_name} on ${day.date}`);
          } catch (error) {
            console.error(`❌ Failed to delete ${schedule.camera_name} on ${day.date}:`, error);
          }
        }
      }
      
      // Refresh data
      loadAllScheduledRecordings();
      setDeleteAllDialogOpen(false);

    } catch (error) {
      console.error('❌ Failed to delete all scheduled recordings:', error);
      alert('Fehler beim Löschen aller geplanten Aufnahmen');
    } finally {
      setDeletingAll(false);
    }
  };

  const cancelDeleteAll = () => {
    setDeleteAllDialogOpen(false);
  };

  if (loading || loadingSchedules) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  const totalScheduledClips = getTotalScheduledClips();
  const scheduledCamerasCount = getScheduledCamerasCount();

  return (
    <Box>
      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="text.primary">
                {totalCameras}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Gesamte Kameras
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="text.primary">
                {totalRecordings}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Gesamt Aufnahmen
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary">
                {scheduledCamerasCount}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Kameras mit Planung
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="text.primary">
                {totalScheduledClips}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Geplante Clips
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                size="small"
                disabled={loadingSchedules || loadingActive}
              >
                Aktualisieren
              </Button>
              {totalScheduledClips > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteAll}
                  size="small"
                  disabled={loadingSchedules || loadingActive || deletingAll}
                >
                  Alle löschen
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>



      {totalScheduledClips === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Keine geplanten Aufnahmen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Klicken Sie auf "Geplante Aufnahme" bei einer Kamera auf dem Live-Tab, um automatische Aufnahmen zu planen.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {accounts.map((account) => {
            const accountSchedules = account.cameras
              .map((camera: AccountCamera) => scheduledRecordings.get(camera.id))
              .filter((schedule): schedule is CameraSchedule => Boolean(schedule && schedule.total_scheduled_days > 0));

            if (accountSchedules.length === 0) return null;

            const accountTotalClips = accountSchedules.reduce(
              (sum: number, schedule: CameraSchedule) => sum + (schedule.scheduled_days || []).reduce((daySum: number, day: ScheduledDay) => daySum + (day?.total_clips || 0), 0), 0
            );

            return (
              <Accordion
                key={account.account_id}
                expanded={expandedAccounts.has(account.account_id)}
                onChange={() => handleToggleAccount(account.account_id)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        {account.account_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {accountSchedules.length} Kameras mit geplanten Aufnahmen
                      </Typography>
                    </Box>
                    <Chip
                      label={`${accountTotalClips} Clips`}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  {accountSchedules.map((schedule: CameraSchedule) => (
                    <Card key={schedule.camera_id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {schedule.camera_name}
                        </Typography>
                        
                        {(schedule.scheduled_days || []).map((day: ScheduledDay) => (
                          <Box key={day.date} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1">
                                📅 {new Date(day.date).toLocaleDateString('de-DE', {
                                  weekday: 'long',
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })} ({day.total_clips} Clips)
                              </Typography>
                              <Tooltip title="Geplante Aufnahmen für diesen Tag löschen">
                                <IconButton 
                                  color="error" 
                                  size="small"
                                  onClick={() => handleDeleteDay(schedule.camera_id, schedule.camera_name, day.date)}
                                  disabled={deleting}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Zeit</TableCell>
                                    <TableCell>Dauer</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Datei</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(day.clips || []).slice(0, 5).map((clip: ScheduledClip) => (
                                    <TableRow key={clip._id}>
                                      <TableCell>
                                        {formatDateTime(clip.scheduled_date, clip.filename)}
                                      </TableCell>
                                      <TableCell>
                                        {Math.round(clip.duration_seconds / 60)}min
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          icon={getStatusIcon(clip.status)}
                                          label={clip.status}
                                          color={getStatusColor(clip.status)}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                          {clip.filename}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {(day.clips || []).length > 5 && (
                                    <TableRow>
                                      <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          ... und {(day.clips || []).length - 5} weitere Clips
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Geplante Aufnahmen löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Sind Sie sicher, dass Sie alle geplanten Aufnahmen für <strong>{deleteTarget?.camera_name}</strong> am{' '}
            <strong>
              {deleteTarget?.date && new Date(deleteTarget.date).toLocaleDateString('de-DE', {
                weekday: 'long',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </strong>{' '}
            löschen möchten?
            <br /><br />
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleting}>
            Abbrechen
          </Button>
          <Button 
            onClick={confirmDeleteDay} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Lösche...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={cancelDeleteAll}
        aria-labelledby="delete-all-dialog-title"
        aria-describedby="delete-all-dialog-description"
      >
        <DialogTitle id="delete-all-dialog-title">
          Alle geplanten Aufnahmen löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-all-dialog-description">
            Sind Sie sicher, dass Sie <strong>ALLE geplanten Aufnahmen</strong> löschen möchten, die noch nicht abgeschlossen sind?
            <br /><br />
            Das betrifft <strong>{totalScheduledClips} Clips</strong> von <strong>{getScheduledCamerasCount()} Kameras</strong>.
            <br /><br />
            ⚠️ <strong>Diese Aktion kann nicht rückgängig gemacht werden!</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteAll} disabled={deletingAll}>
            Abbrechen
          </Button>
          <Button 
            onClick={confirmDeleteAll} 
            color="error" 
            variant="contained"
            disabled={deletingAll}
            startIcon={deletingAll ? <CircularProgress size={16} /> : <Delete />}
          >
            {deletingAll ? 'Lösche alle...' : 'ALLE LÖSCHEN'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledRecordingsList; 